import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatRupiah, imageUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useCategories } from "@/hooks/useCategories";
import ShareButtons from "./ShareButtons";

export default function Catalog({ filter, setFilter }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [highlight, setHighlight] = useState(null);
  const { categories, labels } = useCategories();
  const { add } = useCart();

  useEffect(() => {
    api
      .get("/products")
      .then((r) => setProducts(r.data))
      .catch(() => toast.error("Gagal memuat katalog produk"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (products.length === 0) return undefined;
    const apply = () => {
      const m = window.location.hash.match(/^#produk-(.+)$/);
      if (!m || !products.some((p) => p.id === m[1])) return;
      setFilter("semua");
      setHighlight(m[1]);
      setTimeout(() => document.getElementById(`produk-${m[1]}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
    };
    apply();
    window.addEventListener("hashchange", apply);
    return () => window.removeEventListener("hashchange", apply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  const visible =
    filter === "semua" ? products : products.filter((p) => p.category === filter);

  return (
    <section id="katalog" className="py-20 bg-[#F4EFE6] border-b border-[#E8E2D5]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-xl mx-auto mb-10">
          <span className="text-xs uppercase tracking-widest text-[#5F755E] font-semibold">
            Produk Unggulan Siap Checkout
          </span>
          <h2 className="font-serif-display text-3xl sm:text-4xl font-normal text-[#1E2B1D] mt-2">
            Katalog Bunga Puspita
          </h2>
          <p className="text-xs sm:text-sm text-[#5E6D5B] mt-3">
            Pilih rangkaian favorit Anda, masukkan ke keranjang, dan lanjutkan ke checkout untuk
            pembayaran transfer bank.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-10">
          <button
            data-testid="filter-semua"
            onClick={() => setFilter("semua")}
            className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors ${
              filter === "semua"
                ? "bg-[#3D5A40] text-[#FBF9F5]"
                : "bg-[#FBF9F5] text-[#3D5A40] border border-[#D5CCBC] hover:border-[#3D5A40]"
            }`}
          >
            Semua
          </button>
          {categories.map((c) => (
            <button
              key={c.key}
              data-testid={`filter-${c.key}`}
              onClick={() => setFilter(c.key)}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors ${
                filter === c.key
                  ? "bg-[#3D5A40] text-[#FBF9F5]"
                  : "bg-[#FBF9F5] text-[#3D5A40] border border-[#D5CCBC] hover:border-[#3D5A40]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p data-testid="catalog-loading" className="text-center text-sm text-[#6B7C66] py-10">
            Memuat katalog bunga...
          </p>
        ) : visible.length === 0 ? (
          <p data-testid="catalog-empty" className="text-center text-sm text-[#6B7C66] py-10">
            Belum ada produk pada kategori ini.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {visible.map((p) => (
              <div
                key={p.id}
                id={`produk-${p.id}`}
                data-testid={`product-card-${p.id}`}
                className={`bg-[#FBF9F5] rounded-2xl p-5 border shadow-sm flex flex-col justify-between transition-shadow ${
                  highlight === p.id ? "border-[#3D5A40] ring-2 ring-[#3D5A40]/30 shadow-lg" : "border-[#E8E2D5]"
                }`}
              >
                <div>
                  <div className="aspect-[4/3] rounded-xl relative overflow-hidden mb-4 bg-[#EAE2D2]">
                    <img
                      src={imageUrl(p.image_url)}
                      alt={p.name}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <span className="absolute top-3 left-3 text-[11px] font-bold text-[#1E2B1D] bg-[#FBF9F5] px-2.5 py-1 rounded-full shadow-sm">
                      {labels[p.category] || p.category}
                    </span>
                    <span
                      data-testid={`product-stock-${p.id}`}
                      className={`absolute top-3 right-3 text-[11px] font-medium px-2 py-0.5 rounded-full ${
                        p.stock > 0
                          ? "text-[#3D5A40] bg-[#EAF2E9]"
                          : "text-red-700 bg-red-100"
                      }`}
                    >
                      {p.stock > 0 ? `Stok: ${p.stock}` : "Stok Habis"}
                    </span>
                  </div>
                  <h3 className="font-serif-display text-lg font-bold text-[#1E2B1D]">{p.name}</h3>
                  <p className="text-xs text-[#6B7C66] mt-1.5 leading-relaxed">{p.description}</p>
                </div>
                <div className="mt-6 pt-4 border-t border-[#E8E2D5]">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <span className="text-[10px] uppercase tracking-wider text-[#7E8F7A] block">
                        Harga Produk
                      </span>
                      <span data-testid={`product-price-${p.id}`} className="text-base font-bold text-[#203120] whitespace-nowrap">
                        {formatRupiah(p.price)}
                      </span>
                    </div>
                    <button
                      data-testid={`product-add-${p.id}`}
                      onClick={() => {
                        add(p);
                        toast.success(`${p.name} masuk keranjang`);
                      }}
                      disabled={p.stock <= 0}
                      className="px-4 py-2 rounded-xl bg-[#3D5A40] hover:bg-[#2E4530] disabled:opacity-40 disabled:cursor-not-allowed text-[#FBF9F5] text-xs font-semibold transition-colors shadow-sm whitespace-nowrap"
                    >
                      + Keranjang
                    </button>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <span className="text-[10px] uppercase tracking-wider text-[#7E8F7A]">Bagikan produk</span>
                    <ShareButtons product={p} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-12 text-center">
          <p className="text-xs text-[#6B7C66] mb-3">
            Butuh rangkaian kustom atau ukuran khusus untuk dekorasi panggung & wedding?
          </p>
          <a
            href="#layanan"
            data-testid="catalog-custom-link"
            className="inline-flex items-center gap-2 text-xs font-bold text-[#3D5A40] hover:underline"
          >
            Konsultasi Rangkaian Khusus →
          </a>
        </div>
      </div>
    </section>
  );
}
